import React from 'react';
import HashtagSelector from '../../shared/components/HashtagSelector';
import { useTranslation } from 'react-i18next';

interface SidebarProps {
  selectedHashtags: string[];
  onHashtagChange: (tags: string[]) => void;
  onCancel: () => void;
  onPost: () => void;
  isPostDisabled?: boolean;
  /** Optional fixed width (px). Defaults to 240. */
  widthPx?: number;
  /** Sticky offset to match header height. Defaults to 64. */
  stickyTopPx?: number;
}

const SidebarGalleryEditPosting: React.FC<SidebarProps> = ({
  selectedHashtags,
  onHashtagChange,
  onCancel,
  onPost,
  isPostDisabled = false,
  widthPx = 225,
  stickyTopPx = 64,
}) => {
  const { t } = useTranslation();
  const disabled = !!isPostDisabled;

  return (
    <div
      style={{
        ...sidebarStyle,
        width: widthPx,
        top: `${stickyTopPx}px`,
      }}
    >
      <h2 style={headingStyle}>🖼 {t('galleryEditSidebar.heading')}</h2>

      <HashtagSelector
        context="gallery"
        selectedTags={selectedHashtags}
        onChange={onHashtagChange}
      />

      {/* spacer pushes buttons to the bottom */}
      <div style={{ flex: 1 }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Save / Update button – same pill / gradient style as posting */}
        <button
          onClick={onPost}
          disabled={disabled}
          style={{
            ...buttonStyleBase,
            background: disabled
              ? 'linear-gradient(135deg,#6b7280,#4b5563)'
              : 'linear-gradient(135deg,#22c55e,#a3e635)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.7 : 1,
          }}
        >
          🚀 {t('galleryEditSidebar.buttons.save')}
        </button>

        {/* Cancel – same dark secondary button style */}
        <button
          onClick={onCancel}
          style={{
            ...buttonStyleBase,
            background: 'rgba(15,23,42,0.9)',
            color: '#e5e7eb',
            border: '1px solid rgba(148,163,184,0.6)',
          }}
        >
          ❌ {t('galleryEditSidebar.buttons.cancel')}
        </button>
      </div>
    </div>
  );
};

/** 🔁 Same look as SidebarGalleryPosting */
const sidebarStyle: React.CSSProperties = {
  height: 'calc(92vh - 78px)',
  background:
    'linear-gradient(145deg, rgba(4, 7, 15, 0.95), rgba(15,23,42,0.99))',
  color: '#e5e7eb',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  position: 'sticky',
  top: '120px', // will be overridden by inline top from props
  borderRadius: 20,
  border: '1px solid rgba(148,163,184,0.35)',
  boxShadow: '0 22px 50px rgba(0,0,0,0.8)',
  padding: 20,
};

const buttonStyleBase: React.CSSProperties = {
  padding: '10px 12px',
  width: '100%',
  border: 'none',
  borderRadius: 999,
  cursor: 'pointer',
  color: '#0b1120',
  fontSize: 14,
  fontWeight: 600,
};

const headingStyle: React.CSSProperties = {
  marginBottom: 8,
  fontSize: 18,
  letterSpacing: -0.02,
};

export default SidebarGalleryEditPosting;
